import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    Button,
    CircularProgress, 
    MenuItem,
    FormControl, 
    Select, 
    InputLabel,
} from '@mui/material';
import {
    ArrowBackIosNew,
    FileCopy,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { getSelectedIds } from 'state/helpers';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DataGridComponent from 'components/DataGridComponent';
import SetPermissionList from './SetPermissionList';


const SetPermissions = () => {
    useAuthCheck();
    const { userId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();    
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const scrollRef = useRef(null);
    const [selectedRights, setSelectedRights] = useState({});
    const [state, setState] = useState({
        loading: false,
        error: null,
        user: null,
        users: [],
        permissions: [],
        selectedSourceUser: null,
    });
    const [rowSelectionModel, setRowSelectionModel] = useState({
        type: 'include',
        ids: new Set(),
    });
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


    const apiCalls = useMemo(() => ({
        fetchUser: async () => {
          if (!userId) return null;
          const res = await api.get(`/users/${userId}`);
          return res.data;
        },
        fetchUsers: async () => {
          const res = await api.get('/users/active');
          return res.data;
        },
        fetchPermissions: async () => {
          const res = await api.get('/permissions/visible');
          return res.data;
        }
    }), [userId]);
            
    useEffect(() => {
        const fetchData = async () => {
            setState(prev => ({ ...prev, loading: true, error: null }));

            try {
                const [user, users, permissions] = await Promise.all([
                    apiCalls.fetchUser(),
                    apiCalls.fetchUsers(),
                    apiCalls.fetchPermissions()
                ]);
                
                if (user.permissions?.length > 0) {
                    const rightsMap = {};
                    const selectedIds = new Set();

                    user.permissions?.forEach((perm) => {
                        const pid = perm.permissionId?.toString();
                        if (!pid) return;
                        selectedIds.add(pid); 

                        if (!rightsMap[pid]) rightsMap[pid] = {};

                        // Scan right[] & map { rightName: hasRight }
                        perm.right?.forEach((r) => {
                            rightsMap[pid][r.rightName] = r.hasRight;
                        });
                    });
                    
                    setSelectedRights(rightsMap);
                    setRowSelectionModel({ type: "include", ids: selectedIds });
                }

                setState(prev => ({
                    ...prev,
                    loading: false,
                    user,
                    users,
                    permissions,
                }));
            } catch (error) {
                setState(prev => ({ ...prev, loading: false, error }));
            }
        };

        fetchData();
    }, [apiCalls]);
    
    const columns = [
        {
            field: "no",
            headerName: "No",
            ...(() => {
                return isMobile ? { width: 60 } : { flex: 1 };
            })(),
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
        },
        { 
            field: "element", 
            headerName: "Element", 
            ...(() => {
                return isMobile ? { width: 120 } : { flex: 2 };
            })(),
        },
        { 
            field: "path", 
            headerName: "Path", 
            ...(() => {
                return isMobile ? { width: 120 } : { flex: 2 };
            })(),
        },
        {
            field: "description",
            headerName: "Description",
            ...(() => {
                return isMobile ? { width: 250 } : { flex: 4 };
            })(), 
        },
        {
            field: "category",
            headerName: "Category",
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 2 };
            })(),     
        },
        {
            field: 'right',
            headerName: 'Right',
            ...isMobile ? { width: 120 } : { flex: 2 },
            renderCell: (params) => {
                const permissionId = params.row._id;
                const rights = params.row.right || [];

                return (
                    <Box
                        sx={{
                            height: '100%',
                            alignItems: 'flex-start',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                        }}
                    >
                        {rights.map((item, index) => {
                            const checked = selectedRights?.[permissionId]?.[item?.rightName] || false;

                            return (
                                <Box
                                    key={index}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    sx={{ mb: 0.5 }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                            const newVal = e.target.checked;
                                            setSelectedRights((prev) => ({
                                                ...prev,
                                                [permissionId]: {
                                                    ...prev[permissionId],
                                                    [item.rightName]: newVal,
                                                },
                                            }));
                                            setRowSelectionModel((prev) => {
                                                if (prev.ids.has(permissionId)) return prev;
                                                return { type: 'include', ids: new Set([...prev.ids, permissionId]) };
                                            });
                                        }}
                                    />
                                    <Typography variant="body2">{item?.rightName}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                );
            },
        },
    ];
    
    const handleSave = useCallback(async () => {
        if (!userId) {
            toast.error("User not found!");
            return;
        }

        try {
            const groupedPermissions = [];

            const selectedIds = getSelectedIds(rowSelectionModel, state.permissions);
            
            for (const id of selectedIds) {
                const module = state.permissions?.find(p => p._id === id);
                if (!module) continue;

                const rightsList = module.right || [];
                const moduleRights = selectedRights?.[id] || {};

                const rightArray = rightsList.map(r => ({
                    rightName: r.rightName,
                     hasRight: state.user?.role === "admin"
                        ? true
                        : r.rightName === "read"
                            ? true
                            : moduleRights[r.rightName] || false,
                }));

                groupedPermissions.push({
                    permissionId: id,
                    right: rightArray,
                });
            }

            await toast.promise(
                api.post(`/users/${userId}/permissions`,
                    { permissions: groupedPermissions }
                ),
                {
                    loading: "Saving permissions...",
                    success: "Permissions saved successfully.",
                    error: (err) => err.response?.data?.message || "Failed to save permissions!",
                }
            );

            navigate("/users");
        } catch (error) {
            toast.error(error.message || "Failed to save permissions!");
        }
    }, [state.user,
        userId, 
        state.permissions, 
        selectedRights, 
        rowSelectionModel, 
        navigate
    ]);
    
    const handleCopyUsers = useCallback((selectedUserId) => {
    if (!selectedUserId) {
        toast.error("Please select a user to copy permissions.");
        return;
    }

    const sourceUser = state.users.find(user => user._id === selectedUserId);
        if (sourceUser?.permissions?.length) {
            const rightsMap = {};
            const selectedIds = new Set();

            sourceUser.permissions.forEach((perm) => {
                const pid = perm.permissionId?.toString();
                if (!pid) return;
                selectedIds.add(pid);
                if (!rightsMap[pid]) rightsMap[pid] = {};

                // Lặp qua từng quyền trong mảng `right`
                perm.right?.forEach((r) => {
                    rightsMap[pid][r.rightName] = r.hasRight;
                });
            });

            // Cập nhật cả 2 state: quyền chi tiết + danh sách permission đã chọn
            setSelectedRights(rightsMap);
            setRowSelectionModel({ type: 'include', ids: selectedIds });

            toast.success("Permissions copied successfully.");
        } else {
            toast.error("No permissions found!");
        }
    }, [state.users]);

    if (state.loading) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <CircularProgress />
        </FlexCenter>
    );
    
    if (state.error) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <Typography color="error">{state.error?.message}</Typography>
        </FlexCenter>
    );

    
    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header 
                    title="Authorization" 
                    subtitle={state.loading 
                        ? 'Loading...' 
                        : state.user && state.user?.lastName && state.user?.firstName 
                          ? `${(state.user.lastName + ' ' + state.user.firstName).toUpperCase()}` 
                          : 'Set Permissions'
                      }
                />
                {!state.loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl 
                            variant="filled" 
                            sx={{
                                minWidth: '6rem',
                                width: isMobile ? '6.8rem' : '16rem',
                                '& .MuiInputBase-root': {
                                    height: 36,         
                                    fontSize: '0.75rem' 
                                },
                                '& .MuiInputLabel-root': {
                                    top: -5,             
                                    fontSize: '0.75rem', 
                                }
                            }}
                        >
                            <InputLabel>Copy from</InputLabel>
                            <Select
                                value={state.selectedSourceUser || ''}
                                onChange={(e) => setState(prev => ({ ...prev, selectedSourceUser: e.target.value }))}
                            >
                                <MenuItem value="">
                                    <em>Select a user to copy</em>
                                </MenuItem>
                                {state.users
                                    .filter(user => user._id !== userId) // Exclude current user
                                    .map(user => (
                                        <MenuItem key={user._id} value={user._id}>
                                            {user.email}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        
                        <StyledButton 
                            disabled={!state.selectedSourceUser}
                            onClick={() => handleCopyUsers(state.selectedSourceUser)}
                        >
                            <FileCopy />
                        </StyledButton>
                        <StyledButton 
                            onClick={() => navigate("/users")} 
                        >
                            <ArrowBackIosNew />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>            

            <Box
                display="grid"
                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                gap={2}
            > 
                <Box
                    ref={scrollRef}
                    height={LAYOUT_HEIGHT}  
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
                >                                           
                    {isMobile ? (
                        <SetPermissionList
                            permissions={state.permissions}
                            selectedRights={selectedRights}
                            setSelectedRights={setSelectedRights}
                            rowSelectionModel={rowSelectionModel}
                            setRowSelectionModel={setRowSelectionModel}
                        />
                    ) : (
                        <DataGridComponent
                            rows={state.permissions || []}
                            columns={columns}
                            columnVisibilityModel={columnVisibilityModel}
                            setColumnVisibilityModel={setColumnVisibilityModel}
                            loading={state.loading}
                            props={{
                                initialState: { pagination: { paginationModel: { page: 0, pageSize: 100 } } },
                                checkboxSelection: true,
                                disableRowSelectionOnClick: true,
                                keepNonExistentRowsSelected: true,
                                onRowSelectionModelChange: setRowSelectionModel,
                                rowSelectionModel,
                            }}
                        />
                    )}

                    <Button
                        fullWidth
                        aria-label="Save"
                        type="submit"
                        variant="contained"
                        color="secondary"
                        disabled={rowSelectionModel.type === 'include' 
                            ? rowSelectionModel.ids.size === 0  
                            : false                              
                        }
                        onClick={handleSave}
                        sx={{ height: 42 }}
                    >
                        Save
                    </Button>

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            </Box>  
        </Box>
    );
};

export default SetPermissions;
